import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Compass, GraduationCap, LogOut } from 'lucide-react';
import { isAdminAuthenticated, logoutAdmin } from '@/lib/adminAuth';
import { getAdminBase, realmForBase } from '@/lib/adminBase';
import TrainingAcademy from '@/components/training/TrainingAcademy';

/**
 * /albaniaadmin/training (and /romaniaadmin/training) — the Social Media Hub Academy.
 * Interactive slide-based training + certification quiz for administrators,
 * doubling as a premium client-facing product demonstration.
 */
const TrainingAcademyPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const base = getAdminBase(location.pathname);
  const realm = realmForBase(base);

  useEffect(() => {
    if (!isAdminAuthenticated(realm)) navigate(base, { replace: true });
  }, [navigate, base, realm]);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* ambient glow to match the admin design language */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 left-1/4 h-96 w-96 rounded-full bg-[#C9A961]/10 blur-[140px]" />
        <div className="absolute bottom-0 right-1/4 h-96 w-96 rounded-full bg-[#1A3B5C]/20 blur-[140px]" />
      </div>

      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 py-4 lg:px-10">
          <button onClick={() => navigate(`${base}/dashboard?view=social`)} className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/15 bg-white/[0.04]">
              <GraduationCap className="h-4 w-4 text-[#C9A961]" />
            </div>
            <div className="text-left">
              <div className="text-sm font-medium tracking-tight text-white">Hub Academy</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">{base.replace('/', '')}</div>
            </div>
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`${base}/dashboard?view=social`)}
              className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs text-white/70 hover:text-white"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Social Media Hub</span>
              <Compass className="h-3.5 w-3.5 sm:hidden" />
            </button>
            <button
              onClick={() => { logoutAdmin(); navigate(base, { replace: true }); }}
              className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs text-white/70 hover:border-red-400/40 hover:text-red-300"
            >
              <LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="relative">
        <TrainingAcademy realm={realm} />
      </main>
    </div>
  );
};

export default TrainingAcademyPage;
